import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { GetAppQr } from '~/components/ui/GetAppQr';
import { AppModalContext } from '~/lib/appModal';
import { cn } from '~/lib/cn';
import { useContent } from '~/lib/useLanguage';

/**
 * Provides the app-download QR modal to the whole page. Desktop "Get the app"
 * triggers (see ButtonLink) open it via the AppModalContext; mobile CTAs skip
 * this and go straight to the store. The QR encodes the current host's
 * `/get-the-app` (UA redirect). The modal rises from the bottom over a shared
 * blur backdrop (matching the mega menu).
 */
export function AppDownloadProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  // Escape closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, closeModal]);

  return (
    <AppModalContext.Provider value={openModal}>
      {children}
      <AppDownloadModal open={open} onClose={closeModal} />
    </AppModalContext.Provider>
  );
}

function AppDownloadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Opened by the header CTA, so it inherits whatever language that page is in.
  const { navigation, ui } = useContent();
  // Cursor-following close button: appears only while hovering the backdrop
  // (outside the QR card) and tracks the pointer.
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const [overCard, setOverCard] = useState(false);
  const [tracking, setTracking] = useState(false);
  const rafRef = useRef<number | null>(null);

  // Reset the follower whenever the modal is closed.
  useEffect(() => {
    if (!open) {
      setTracking(false);
      setOverCard(false);
    }
  }, [open]);

  const onPointerMove = (e: React.PointerEvent) => {
    const { clientX, clientY } = e;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setCursor({ x: clientX, y: clientY });
      setTracking(true);
    });
  };

  const followerVisible = open && tracking && !overCard;

  return (
    <div
      inert={!open}
      onPointerMove={onPointerMove}
      className={cn(
        'fixed inset-0 z-[100] flex items-end justify-center px-5 pb-[12vh] sm:items-center sm:pb-0',
        open ? '' : 'pointer-events-none',
      )}>
      {/* Shared blur backdrop (same tint/blur as the mega menu). Blur applies
          instantly on open — no opacity fade — so it never "ramps up".
          Pointer cursor signals it's clickable (closes the modal). */}
      <button
        type="button"
        aria-label={ui.close}
        onClick={onClose}
        className={cn(
          'absolute inset-0 cursor-pointer bg-blur-backdrop/60 backdrop-blur-sm',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Card rises up from the bottom. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={navigation.getAppCta.label}
        onPointerEnter={() => setOverCard(true)}
        onPointerLeave={() => setOverCard(false)}
        className={cn(
          'relative transition-all duration-500 ease-nav',
          open ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0',
        )}>
        <div className="flex w-60 flex-col items-center gap-10 rounded-[32px] bg-white px-8 pt-10 pb-8">
          {/* Title + QR fade out faster than the card's slide/fade, so the
              contents blend out first for a cleaner close. */}
          <p
            className={cn(
              'text-h4 text-ink transition-opacity duration-200 ease-nav',
              open ? 'opacity-100' : 'opacity-0',
            )}>
            {navigation.getAppCta.label}
          </p>
          <GetAppQr
            className={cn(
              'w-full transition-opacity duration-200 ease-nav',
              open ? 'opacity-100' : 'opacity-0',
            )}
          />
        </div>
      </div>

      {/* Close button that follows the cursor over the backdrop. It's
          pointer-events-none so clicks pass through to the backdrop below. */}
      <div
        aria-hidden
        style={{ left: cursor.x, top: cursor.y }}
        className={cn(
          'pointer-events-none fixed z-[110] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150',
          followerVisible ? 'opacity-100' : 'opacity-0',
        )}>
        <div className="flex size-14 items-center justify-center rounded-full bg-white text-grey-900">
          <svg
            viewBox="0 0 24 24"
            className="size-5"
            stroke="currentColor"
            strokeWidth="2"
            fill="none">
            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
